import { Injectable, signal } from '@angular/core';
import { OmniMqttService } from '../../../core/mqtt/omni-mqtt.service';
import { ControlAction } from '../../../core/mqtt/omni-protocol.interface';
import { MQTT_CONSTANTS } from '../../../core/mqtt/mqtt.config';
import { DEVICE_STATIC_CONFIG } from '../data/device-config.data';
import { resolveDeviceNamesFromPoint } from '../../../core/mqtt/omni-mqtt-mapping.data';

export type DeviceType = 'AC' | 'LIGHT' | 'CCTV' | 'ONVIF' | 'FAN' | 'DOOR' | 'POWER' | 'AIR_QUALITY' | 'RB' | 'PANEL' | 'HOST';
export type DeviceStatus = 'NORMAL' | 'ALARM' | 'MAINTENANCE' | 'OFFLINE';

export function getDeviceIcon(type: string): string {
    switch (type) {
        case 'AC': return 'ci-air-conditioner';
        case 'LIGHT': return 'ci-lighting';
        case 'CCTV':
        case 'ONVIF': return 'ci-cctv';
        case 'AIR_QUALITY': return 'ci-air-quality';
        case 'FAN': return 'ci-fan';
        case 'DOOR': return 'ci-access-control';
        case 'POWER': return 'ci-electricity';
        case 'RB': return 'ci-roller-blind';
        case 'PANEL': return 'ci-panel';
        case 'HOST': return 'ci-device';
        default: return 'ci-device';
    }
}

export interface Device {
    id: string;
    name: string;
    code: string;
    type: DeviceType;
    status: DeviceStatus;
    floor: number;
    position: [number, number];
    angle?: number;
    temperature?: number;
    humidity?: number;
    co2?: number;
    pm25?: number;
    powerUsage?: number;
    imageUrl?: string;
    config?: any[]; // RawConfig[] from MQTT
    acSubId?: string;
    data?: Record<string, {
        value: string | number | null;
        timestamp: number;
        updateTimestamp?: number;
        type?: number;
        name?: string;
        rawObjName?: string;
        rawId?: string;
        rawTargetId?: string;
        isAlarm?: boolean;
        operator?: string;
    }>; // Real-time values and extended Metadata
    totalWatt?: number;
    deviceId?: string;
    isOnline?: boolean;
}

@Injectable({
    providedIn: 'root',
})
export class DeviceService {
    private loadStaticConfig(): Device[] {
        const baseConfig = DEVICE_STATIC_CONFIG;
        try {
            const saved = localStorage.getItem('map-device-config');
            if (saved) {
                const parsed = JSON.parse(saved);
                if (Array.isArray(parsed) && parsed.length > 0) {
                    console.log('[DeviceService] 從 localStorage 載入暫存的設備座標設定 (僅同步座標與角度)');
                    return baseConfig.map(device => {
                        const savedDevice = parsed.find((d: any) => d.code === device.code);
                        if (savedDevice) {
                            return {
                                ...device,
                                position: savedDevice.position,
                                angle: savedDevice.angle
                            };
                        }
                        return device;
                    });
                }
            }
        } catch (e) {
            console.warn('[DeviceService] 載入 localStorage 設定失敗，將使用預設設定', e);
        }
        return baseConfig;
    }

    private staticConfig: Device[] = this.loadStaticConfig();

    devices = signal<Device[]>(this.staticConfig);
    private activeAlarms = new Set<string>();
    private activeAlarmPoints = new Map<string, Set<string>>(); // deviceName -> Set<funId>

    private resolveDeviceStatus(deviceData: Record<string, any> | undefined, isAlarming: boolean): DeviceStatus {
        const statusVal = deviceData?.['Status']?.value;
        if (statusVal === 2 || statusVal === 3) return 'MAINTENANCE';

        if (isAlarming) return 'ALARM';

        // 系統平面圖上沒有離線的顏色判定，因此移除 !isOnline 判斷，預設皆為 NORMAL
        return 'NORMAL';
    }

    constructor(private mqttService: OmniMqttService) {
        // 攔截並紀錄目前發生告警的設備
        this.mqttService.alarms$.subscribe(alarm => {
            const { deviceName: originalName, function_id, status, closeTime, point } = alarm.payload;

            // 🚨 修正告警設備偏移問題：如果是會議室子設備 (RB/Panel) 的點位，解析出正確的名稱
            const targetNames = resolveDeviceNamesFromPoint(point, originalName);

            // 1. 記錄設備層級告警
            if (status === 0 || closeTime === null) {
                targetNames.forEach(name => this.activeAlarms.add(name));

                // 2. 記錄點位層級告警
                targetNames.forEach(name => {
                    if (!this.activeAlarmPoints.has(name)) {
                        this.activeAlarmPoints.set(name, new Set());
                    }
                    this.activeAlarmPoints.get(name)!.add(function_id);
                });
            } else {
                targetNames.forEach(name => {
                    this.activeAlarms.delete(name);
                    if (this.activeAlarmPoints.has(name)) {
                        this.activeAlarmPoints.get(name)!.delete(function_id);
                    }
                });
            }

            // 即時更新目前的 device 狀態，不須等待下一包 table
            const current = this.devices().map(d => {
                // 檢查此設備是否在受影響的名單中
                const isAffected = targetNames.some(name => d.name === name || name.includes(d.name));

                if (isAffected) {
                    // 找出該設備對應的所有告警點位
                    const matchedTargetName = targetNames.find(name => d.name === name || name.includes(d.name));
                    const alarmingPoints = this.activeAlarmPoints.get(matchedTargetName!) || new Set();

                    // 更新點位狀態
                    if (d.data) {
                        Object.keys(d.data).forEach(funId => {
                            d.data![funId].isAlarm = alarmingPoints.has(funId);
                        });
                    }

                    const newStatus = this.resolveDeviceStatus(d.data, this.activeAlarms.has(matchedTargetName!));
                    return { ...d, status: newStatus };
                }
                return d;
            });
            this.devices.set(current);
        });

        // 🚨 初始載入歷史告警清單：將所有 status=0 (未解除) 的告警加入 activeAlarms
        this.mqttService.alarmList$.subscribe(alarmList => {
            if (alarmList.length === 0) return;

            // 先清空再重建，確保與 Server 狀態同步
            this.activeAlarms.clear();
            this.activeAlarmPoints.clear();

            alarmList.filter(a => a.status === 0).forEach(a => {
                const targetNames = resolveDeviceNamesFromPoint(a.point, a.deviceName);
                targetNames.forEach(name => {
                    this.activeAlarms.add(name);
                    if (!this.activeAlarmPoints.has(name)) {
                        this.activeAlarmPoints.set(name, new Set());
                    }
                    this.activeAlarmPoints.get(name)!.add(a.function_id);
                });
            });

            // 觸發一次 devices 重算，讓 marker 顏色立即更新
            const current = this.devices().map(d => {
                const deviceAlarms = [...this.activeAlarmPoints.entries()].find(
                    ([name]) => d.name === name || name.includes(d.name)
                );
                const alarmingPoints = deviceAlarms ? deviceAlarms[1] : new Set<string>();
                const isAlarming = alarmingPoints.size > 0;

                // 更新點位狀態
                if (d.data) {
                    Object.keys(d.data).forEach(funId => {
                        d.data![funId].isAlarm = alarmingPoints.has(funId);
                    });
                }

                const newStatus = this.resolveDeviceStatus(d.data, isAlarming);
                return { ...d, status: newStatus };
            });
            this.devices.set(current);
        });

        this.mqttService.deviceTable$.subscribe(table => {
            if (!table) return;

            // 🚀 修正同步邏輯：基於目前的 devices() 狀態進行更新，而不是每次都重置回 staticConfig
            // 這能確保 real-time 的 data 不會被靜態配置覆蓋
            const current = this.devices();
            const updated = current.map(d => {
                // 優先使用 code (uuid) 匹配，再使用名稱匹配
                // 注意：這裡使用 table[d.code] 是因為 OmniMqttService 的 deviceTable 是以 uuid 為 key
                const m = table[d.code] || Object.values(table).find(item =>
                    item.uuid === d.code ||
                    item.name === d.name ||
                    (item.name && item.name.includes(d.name))
                );

                if (m) {
                    // 🔑 告警優先級處理
                    const deviceAlarms = [...this.activeAlarmPoints.entries()].find(
                        ([name]) => d.name === name || name.includes(d.name)
                    );
                    const alarmingPoints = deviceAlarms ? deviceAlarms[1] : new Set<string>();

                    const newStatus = this.resolveDeviceStatus(m.data, alarmingPoints.size > 0);

                    // 同步點位告警狀態
                    if (m.data) {
                        Object.keys(m.data).forEach(funId => {
                            m.data![funId].isAlarm = alarmingPoints.has(funId);
                        });
                    }

                    return {
                        ...d,
                        status: newStatus,
                        isOnline: m.isOnline,
                        config: m.config,
                        data: m.data, // 直接替換為最新的實時數據包
                        acSubId: m.acSubId,
                        totalWatt: m.totalWatt,
                        deviceId: m.deviceId
                    };
                }
                return d; // 若無更新則保留原狀
            });

            this.devices.set(updated);
        });
    }

    getDevicesByFloor(floor: number) {
        return this.devices().filter(d => d.floor === floor);
    }

    // 群控支援的設備類型（需對應後端 Scenes 群控指令名稱）
    private readonly GROUP_CTRL_TYPES = ['LIGHT', 'AC', 'FAN'] as const;

    turnOffFloor(floorNum: number) {
        // 群控整層關閉指令格式：Sys|Scenes|ALL_{FLOOR}F_OFF|1
        const funId = `ALL_${floorNum}F_OFF`;
        const action: ControlAction = {
            objName: 'Sys',
            id: 'Scenes',
            funId: funId,
            value: '1'
        };

        this.mqttService.sendControl(MQTT_CONSTANTS.OMNI_ID, [action]);
        console.log(`[群控] 已發送整層關閉指令 (${floorNum}F): Sys|Scenes|${funId}|1`);
    }

    turnOffDeviceCategory(floorNum: number, categoryType: string) {
        let action: ControlAction;

        if (categoryType === 'FAN') {
            // FAN (全熱交換器) 需透過 MODBUS 實體點位指令關閉，不走 Scenes
            const deviceId = floorNum === 14 ? '10' : '11';
            action = {
                objName: 'MODBUS',
                id: deviceId,
                funId: '01_Power',
                value: '0'
            };
        } else {
            // LIGHT 或 AC 走 Scenes 群控指令
            // 群控指令格式：Sys|Scenes|{TYPE}_ALL_{FLOOR}F_OFF|1
            const funId = `${categoryType}_ALL_${floorNum}F_OFF`;
            action = {
                objName: 'Sys',
                id: 'Scenes',
                funId: funId,
                value: '1'
            };
        }

        this.mqttService.sendControl(MQTT_CONSTANTS.OMNI_ID, [action]);
        console.log(`[群控] 已發送群控關閉指令: ${action.objName}|${action.id}|${action.funId}|${action.value}`);
    }
}
